import {Autocomplete, TextField, TextFieldProps} from '@mui/material';
import isNil from 'lodash/isNil';
import {useMemo} from 'react';

import {useAppSelector} from '../../hooks/redux';

type Props = Pick<
  TextFieldProps,
  | 'size'
  | 'name'
  | 'disabled'
  | 'fullWidth'
  | 'label'
  | 'error'
  | 'helperText'
  | 'InputProps'
  | 'sx'
> & {
  value?: number | null;
  nullLabel?: string;
  disableClearable?: boolean;
  onChange?: (value?: number | null) => void;
};

export const AssetHumanGroupSelect = ({
  value,
  disabled,
  fullWidth,
  size,
  nullLabel,
  disableClearable,
  onChange,
  ...props
}: Props) => {
  const assetHumanGroups = useAppSelector(({assets}) => assets.asset_human_groups);
  const nullValue = -1;

  const options = useMemo(() => {
    if (nullLabel) {
      return [
        {
          id: nullValue,
          name: nullLabel,
          employees: null,
        },
        ...assetHumanGroups,
      ];
    }
    return assetHumanGroups;
  }, [assetHumanGroups, nullLabel]);

  const selectedOptionId = isNil(value) ? nullValue : value;
  const selectedOption = options.find((i) => i.id === selectedOptionId) ?? null;

  return disabled ? (
    <TextField
      {...props}
      value={options.find((i) => i.id === value)?.name ?? value ?? ''}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
    />
  ) : (
    <Autocomplete
      value={selectedOption ?? null}
      disabled={disabled}
      fullWidth={fullWidth}
      options={options}
      size={size}
      disableClearable={disableClearable || selectedOptionId === nullValue}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(o) => o.name ?? ''}
      renderInput={(params) => (
        <TextField label="Group" {...props} {...params} />
      )}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.id}>
            {option.name}
            {option.id !== nullValue
              ? ` (${option.employees?.split(',').length} emp.)`
              : null}
          </li>
        );
      }}
      slotProps={{
        paper: {
          sx: {
            width: 300,
          },
        },
      }}
      onChange={(_, option) => {
        const v =
          !option || isNil(option.id) || option.id === nullValue
            ? null
            : option.id;
        onChange?.(v);
      }}
    />
  );
};
